import {
  type AuthFailureReason,
  type AuthFailureStage,
  type JourneyId,
  journeyAnalytics,
} from "@src/features/analytics/journeyAnalytics"
import { ChainType } from "@src/types/chainType"
import { isAnalyticsSuppressed } from "@src/utils/analyticsSuppression"

export const AUTH_JOURNEY_LEASE_KEY = "defuse-auth-journey-lease"
export const AUTH_JOURNEY_LEASE_TTL_MS = 30 * 60 * 1000

export type AuthJourneyLease = Readonly<{
  v: 1
  journeyId: JourneyId
  method: "wallet" | "passkey"
  action: "connect" | "create" | "sign_in"
  chain?: string
  walletId?: string
  startedAt: number
  expiresAt: number
  startedClaimed: true
}>

export type AuthJourneyStart = Readonly<{
  method: AuthJourneyLease["method"]
  action: AuthJourneyLease["action"]
  chain?: string
  walletId?: string
}>

export type AuthJourneyFailure = Readonly<{
  failureStage: AuthFailureStage
  failureReason: AuthFailureReason
}>

export function getAuthJourneyStart(params: {
  id: ChainType
  connector?: { id: string }
  walletId?: string
  adapterName?: string
}): AuthJourneyStart | null {
  switch (params.id) {
    case ChainType.WebAuthn:
      // Opening the passkey chooser is not the external ceremony. The UI
      // store claims the lease when create/sign-in actually invokes WebAuthn.
      return null
    case ChainType.EVM:
      return params.connector == null
        ? null
        : {
            method: "wallet",
            action: "connect",
            chain: params.id,
            walletId: params.connector.id,
          }
    case ChainType.Solana:
      // With no adapter this only opens the provider's chooser.
      return params.adapterName == null
        ? null
        : {
            method: "wallet",
            action: "connect",
            chain: params.id,
            walletId: params.adapterName,
          }
    case ChainType.Near:
    case ChainType.Stellar:
      return {
        method: "wallet",
        action: "connect",
        chain: params.id,
        ...(params.walletId != null ? { walletId: params.walletId } : {}),
      }
    // TON and Tron resolve their connect calls before their provider state
    // proves that a user actually completed the ceremony. Starting here can
    // therefore leave a lease that an unrelated later restore consumes.
    case ChainType.Ton:
    case ChainType.Tron:
      return null
  }
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function removeStoredLease(target: Storage): void {
  try {
    target.removeItem(AUTH_JOURNEY_LEASE_KEY)
  } catch {
    // Storage is optional. A blocked or unavailable sessionStorage is not an
    // auth failure.
  }
}

function normalizedDimension(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.:-]+/g, "_")
    .slice(0, 64)
  return normalized.length > 0 ? normalized : undefined
}

function normalizedWalletId(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (
    /^0x[a-f0-9]{40}$/i.test(trimmed) ||
    /^[GC][A-Z2-7]{55}$/.test(trimmed) ||
    /^M[A-Z2-7]{68}$/.test(trimmed) ||
    /^[1-9A-HJ-NP-Za-km-z]{32,64}$/.test(trimmed) ||
    /^[a-f0-9]{64}$/i.test(trimmed) ||
    /\.(?:near|testnet)$/i.test(trimmed)
  ) {
    return undefined
  }
  return normalizedDimension(trimmed)
}

function validDimension(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 64 &&
    /^[a-z0-9_.:-]+$/.test(value)
  )
}

function isLease(value: unknown, now: number): value is AuthJourneyLease {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false
  }

  const record = value as Record<string, unknown>
  const keys = Object.keys(record)
  const allowedKeys = [
    "v",
    "journeyId",
    "method",
    "action",
    "chain",
    "walletId",
    "startedAt",
    "expiresAt",
    "startedClaimed",
  ]
  if (keys.some((key) => !allowedKeys.includes(key))) return false

  const startedAt = record.startedAt
  const expiresAt = record.expiresAt
  if (
    record.v !== 1 ||
    typeof record.journeyId !== "string" ||
    record.journeyId.length === 0 ||
    record.journeyId.length > 512 ||
    !["wallet", "passkey"].includes(record.method as string) ||
    !["connect", "create", "sign_in"].includes(record.action as string) ||
    typeof startedAt !== "number" ||
    !Number.isFinite(startedAt) ||
    typeof expiresAt !== "number" ||
    !Number.isFinite(expiresAt) ||
    startedAt > now ||
    expiresAt <= startedAt ||
    expiresAt <= now ||
    expiresAt - startedAt > AUTH_JOURNEY_LEASE_TTL_MS ||
    record.startedClaimed !== true
  ) {
    return false
  }

  if (Object.hasOwn(record, "chain") && !validDimension(record.chain)) {
    return false
  }
  if (
    Object.hasOwn(record, "walletId") &&
    (typeof record.walletId !== "string" ||
      normalizedWalletId(record.walletId) !== record.walletId)
  ) {
    return false
  }

  return true
}

/** Reads and validates the one auth handoff record, deleting stale data. */
export function readAuthJourneyLease(): AuthJourneyLease | null {
  const target = storage()
  if (target === null) return null

  let raw: string | null
  try {
    raw = target.getItem(AUTH_JOURNEY_LEASE_KEY)
  } catch {
    return null
  }
  if (raw === null) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    removeStoredLease(target)
    return null
  }

  if (!isLease(parsed, Date.now())) {
    removeStoredLease(target)
    return null
  }
  return parsed
}

function suppressed(): boolean {
  try {
    return isAnalyticsSuppressed()
  } catch {
    // Privacy checks fail closed. The product auth action still proceeds; it
    // simply gets no analytics handoff.
    return true
  }
}

/**
 * Claims and persists a newly accepted external auth ceremony before its
 * analytics fanout. Replacing the single record represents a real retry.
 */
export function startAuthJourney(
  input: AuthJourneyStart
): AuthJourneyLease | null {
  if (suppressed()) {
    // A suppressed retry must not leave an older public attempt alive for a
    // later unsuppressed bootstrap to consume.
    clearAuthJourneyLease()
    return null
  }

  const startedAt = Date.now()
  const lease: AuthJourneyLease = {
    v: 1,
    journeyId: journeyAnalytics.newId(),
    method: input.method,
    action: input.action,
    ...(normalizedDimension(input.chain) !== undefined
      ? { chain: normalizedDimension(input.chain) }
      : {}),
    ...(normalizedWalletId(input.walletId) !== undefined
      ? { walletId: normalizedWalletId(input.walletId) }
      : {}),
    startedAt,
    expiresAt: startedAt + AUTH_JOURNEY_LEASE_TTL_MS,
    startedClaimed: true,
  }

  const target = storage()
  if (target !== null) {
    try {
      target.setItem(AUTH_JOURNEY_LEASE_KEY, JSON.stringify(lease))
    } catch {
      // A storage failure must not block the external ceremony.
    }
  }

  try {
    journeyAnalytics.started({
      journey: {
        journeyId: lease.journeyId,
        flow: "auth",
        privacy: "public",
        valuation: null,
      },
      surface: "login",
      auth_method: lease.method,
      auth_action: lease.action,
      ...(lease.chain !== undefined ? { chain: lease.chain } : {}),
      ...(lease.walletId !== undefined ? { wallet_id: lease.walletId } : {}),
    })
  } catch {
    // The typed analytics interface is fail-soft; keep this boundary safe too.
  }

  return lease
}

/** Atomically removes a chain-matched handoff before a terminal event. */
export function takeAuthJourneyLease(
  expectedChain: string | null
): AuthJourneyLease | null {
  const target = storage()
  if (target === null) return null

  const lease = readAuthJourneyLease()
  if (lease === null) return null

  const chain = normalizedDimension(expectedChain)
  if (chain === undefined || lease.chain !== chain) {
    removeStoredLease(target)
    return null
  }

  try {
    target.removeItem(AUTH_JOURNEY_LEASE_KEY)
  } catch {
    return null
  }
  return lease
}

export function clearAuthJourneyLease(): void {
  const target = storage()
  if (target !== null) removeStoredLease(target)
}

/** Consumes the lease and emits the one typed auth failure. */
export function failAuthJourney(
  expectedChain: string | null,
  failure: AuthJourneyFailure
): void {
  try {
    const lease = takeAuthJourneyLease(expectedChain)
    if (lease === null) return

    journeyAnalytics.failed({
      journey: {
        journeyId: lease.journeyId,
        flow: "auth",
        privacy: "public",
        valuation: null,
      },
      surface: "login",
      auth_method: lease.method,
      auth_action: lease.action,
      ...(lease.chain !== undefined ? { chain: lease.chain } : {}),
      ...(lease.walletId !== undefined ? { wallet_id: lease.walletId } : {}),
      failure_stage: failure.failureStage,
      failure_reason: failure.failureReason,
    })
  } catch {
    // Analytics must never alter auth behavior.
  }
}

// Same key as the pre-journey `account_created` logger so already-seen
// addresses are not re-counted as new accounts after this migration.
const ACCOUNT_SEEN_KEY_PREFIX = "defuse_account_seen:"

function markAccountSeen(
  address: string | null | undefined
): boolean | undefined {
  if (address == null || address === "") return undefined
  if (typeof window === "undefined") return undefined
  try {
    const key = `${ACCOUNT_SEEN_KEY_PREFIX}${address}`
    if (window.localStorage.getItem(key)) return false
    window.localStorage.setItem(key, "1")
    return true
  } catch {
    return undefined
  }
}

/** Consumes the lease and emits the one typed auth completion. */
export function completeAuthJourney(
  expectedChain: string | null,
  address?: string | null
): void {
  try {
    const lease = takeAuthJourneyLease(expectedChain)
    const accountCreated = markAccountSeen(address)
    if (lease === null) return

    const duration = Math.max(0, Date.now() - lease.startedAt)
    journeyAnalytics.completed({
      journey: {
        journeyId: lease.journeyId,
        flow: "auth",
        privacy: "public",
        valuation: null,
      },
      surface: "login",
      auth_method: lease.method,
      auth_action: lease.action,
      ...(lease.chain !== undefined ? { chain: lease.chain } : {}),
      ...(lease.walletId !== undefined ? { wallet_id: lease.walletId } : {}),
      duration_ms: duration,
      ...(accountCreated !== undefined
        ? { account_created: accountCreated }
        : {}),
    })
  } catch {
    // Analytics must never alter auth/bootstrap behavior.
  }
}
